import { motion } from 'framer-motion';
import { CSSProperties, FC, ReactNode } from 'react';

import {
  VESTING_GRAPH_INTRO_BAR_MS,
  VESTING_GRAPH_INTRO_EASE,
  VESTING_GRAPH_INTRO_LABEL_MS,
  VestingGraphIntroPhase,
} from '../model/vesting-graph.intro';

type VestingGraphIntroRevealVariant = 'fade' | 'grow';

type IntroRevealMotion = {
  opacity: number;
  scaleY?: number;
  y?: number;
};

const hiddenMotion = (variant: VestingGraphIntroRevealVariant): IntroRevealMotion =>
  variant === 'grow' ? { opacity: 0.2, scaleY: 0 } : { opacity: 0, y: 8 };

const shownMotion = (variant: VestingGraphIntroRevealVariant): IntroRevealMotion =>
  variant === 'grow' ? { opacity: 1, scaleY: 1 } : { opacity: 1, y: 0 };

type VestingGraphIntroRevealProps = {
  children?: ReactNode;
  className?: string;
  delayMs: number;
  phase: VestingGraphIntroPhase;
  style?: CSSProperties;
  /** `grow` scales from the bottom — use on washes and rails, not on translated badges. */
  variant?: VestingGraphIntroRevealVariant;
};

/** Overlay captions and columns that follow the same left-to-right intro as the series. */
export const VestingGraphIntroReveal: FC<VestingGraphIntroRevealProps> = ({
  children,
  className,
  delayMs,
  phase,
  style,
  variant = 'fade',
}) => {
  const isPlaying = phase === 'playing';
  const isDone = phase === 'done';
  const hidden = hiddenMotion(variant);
  const shown = shownMotion(variant);

  return (
    <motion.div
      animate={isDone || isPlaying ? shown : hidden}
      className={className}
      initial={isDone ? false : hidden}
      style={{
        ...style,
        ...(variant === 'grow' ? { originX: 0.5, originY: 1 } : {}),
      }}
      transition={{
        delay: isPlaying ? delayMs / 1000 : 0,
        duration: isPlaying
          ? (variant === 'grow' ? VESTING_GRAPH_INTRO_BAR_MS : VESTING_GRAPH_INTRO_LABEL_MS) / 1000
          : 0,
        ease: VESTING_GRAPH_INTRO_EASE,
      }}
    >
      {children}
    </motion.div>
  );
};
